import { getConfig } from './config';

export interface GenerationRequestPayload {
  humanImage?: File;
  petImage?: File;
  humanImages?: File[];
  petImages?: File[];
  mood: string;
  clothingStyle: string;
  numberOfPersons?: number;
  numberOfPets?: number;
  clientId: string;
  rootId?: string;
  accessToken?: string;
  trademark?: string;
  newsletterTest?: boolean;
}

export interface GenerationPreview {
  id: string;
  accessToken?: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  mood: string;
  clothingStyle: string;
  numberOfPersons?: number;
  numberOfPets?: number;
  rootId?: string;
  versions?: GenerationVersion[];
  previewUrl?: string;
  downloadUrl?: string;
  errorMessage?: string;
  versionNumber?: number;
  remainingUses?: number;
  requiresSubscription?: boolean;
}

export interface GenerationVersion {
  id: string;
  accessToken?: string;
  versionNumber: number;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  previewUrl?: string;
}

export interface PaymentLinkPayload {
  productType: 'instant' | 'refined';
  accessToken?: string;
  variantId?: string;
  personalizedNote?: string;
}

export interface CheckoutPayload {
  url: string;
  checkoutUrl: string;
  variantId: string;
  quantity: number;
  attributes: Record<string, string>;
}

function apiUrl(path: string) {
  const base = getConfig().apiBaseUrl.replace(/\/$/, '');
  return `${base}${path}`;
}

function apiBaseUrl() {
  return getConfig().apiBaseUrl.replace(/\/$/, '');
}

function withAccessToken(path: string, accessToken?: string) {
  if (!accessToken) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}token=${encodeURIComponent(accessToken)}`;
}

function withNewsletterTest(path: string, newsletterTest?: boolean) {
  if (!newsletterTest) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}newsletterTest=1`;
}

function normalizeMediaUrl(url?: string) {
  if (!url) return url;
  const trimmed = url.trim();

  if (/^[a-z0-9.-]+\.[a-z]{2,}(?::\d+)?\/.+/i.test(trimmed)) {
    return `https://${trimmed}`;
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.pathname.startsWith('/uploads/')) {
      return `${apiBaseUrl()}${parsed.pathname}${parsed.search}`;
    }
  } catch {
    // Keep relative or malformed values untouched so the caller can surface the failure.
  }

  return trimmed;
}

function normalizePreview(preview: GenerationPreview): GenerationPreview {
  return {
    ...preview,
    previewUrl: normalizeMediaUrl(preview.previewUrl),
    downloadUrl: normalizeMediaUrl(preview.downloadUrl),
    versions: preview.versions?.map((version) => ({
      ...version,
      previewUrl: normalizeMediaUrl(version.previewUrl)
    }))
  };
}

function apiHeaders(contentType?: string) {
  const headers: Record<string, string> = {};

  if (contentType) headers['Content-Type'] = contentType;

  return headers;
}

async function prepareImageForUpload(file: File) {
  const supportedType = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
  if (!supportedType) return file;

  const maxDimension = 2200;
  const targetQuality = 0.88;
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Could not prepare image upload.'));
      img.src = objectUrl;
    });

    const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height));
    if (scale >= 1 && file.size <= 2_500_000) return file;

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round((image.naturalWidth || image.width) * scale));
    canvas.height = Math.max(1, Math.round((image.naturalHeight || image.height) * scale));
    const context = canvas.getContext('2d');
    if (!context) return file;

    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', targetQuality));
    if (!blob || blob.size >= file.size) return file;

    const name = file.name.replace(/\.[^.]+$/, '') || 'portrait-upload';
    return new File([blob], `${name}.jpg`, { type: 'image/jpeg' });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export function isApiErrorCode(error: unknown, code: string) {
  return error instanceof ApiRequestError && error.code === code;
}

async function apiError(response: Response) {
  const body = await response.text();
  try {
    const parsed = JSON.parse(body) as { message?: string | string[]; error?: string; code?: string };
    const message = Array.isArray(parsed.message) ? parsed.message.join(' ') : parsed.message || parsed.error;
    return new ApiRequestError(message || 'Something went wrong. Please try again.', response.status, parsed.code);
  } catch {
    return new ApiRequestError(body || 'Something went wrong. Please try again.', response.status);
  }
}

export async function createGeneration(
  payload: GenerationRequestPayload
): Promise<{ id: string; accessToken?: string; rootId?: string; remainingUses?: number; requiresSubscription?: boolean }> {
  const body = new FormData();
  const humanImages = payload.humanImages?.length ? payload.humanImages : payload.humanImage ? [payload.humanImage] : [];
  const petImages = payload.petImages?.length ? payload.petImages : payload.petImage ? [payload.petImage] : [];
  const preparedHumanImages = await Promise.all(humanImages.map(prepareImageForUpload));
  const preparedPetImages = await Promise.all(petImages.map(prepareImageForUpload));
  preparedHumanImages.forEach((image) => body.append('humanImages', image));
  preparedPetImages.forEach((image) => body.append('petImages', image));
  body.append('mood', payload.mood);
  body.append('clothingStyle', payload.clothingStyle);
  body.append('numberOfPersons', String(payload.numberOfPersons || humanImages.length || 1));
  body.append('numberOfPets', String(payload.numberOfPets || petImages.length || 1));
  body.append('clientId', payload.clientId);
  if (payload.rootId) body.append('rootId', payload.rootId);
  if (payload.accessToken) body.append('accessToken', payload.accessToken);
  if (payload.trademark) body.append('trademark', payload.trademark);
  if (payload.newsletterTest) body.append('newsletterTest', '1');

  const response = await fetch(apiUrl('/image-generation/upload-images'), {
    method: 'POST',
    headers: apiHeaders(),
    body
  });

  if (!response.ok) {
    throw await apiError(response);
  }

  return response.json();
}

export async function getPreview(id: string, accessToken?: string, newsletterTest?: boolean): Promise<GenerationPreview> {
  const path = withNewsletterTest(withAccessToken(`/image-generation/preview/${id}`, accessToken), newsletterTest);
  const response = await fetch(apiUrl(path), {
    cache: 'no-store',
    headers: apiHeaders()
  });
  if (!response.ok) {
    throw await apiError(response);
  }
  return normalizePreview(await response.json());
}

export async function getRemainingUsage(
  clientId: string,
  newsletterTest?: boolean
): Promise<{ remaining: number; limit: number; requiresSubscription: boolean }> {
  const path = withNewsletterTest(`/image-generation/usage/remaining?clientId=${encodeURIComponent(clientId)}`, newsletterTest);
  const response = await fetch(apiUrl(path), {
    cache: 'no-store',
    headers: apiHeaders()
  });

  if (!response.ok) {
    throw await apiError(response);
  }

  return response.json();
}

export async function retryGeneration(
  id: string,
  accessToken: string | undefined,
  clientId: string,
  trademark?: string,
  newsletterTest?: boolean
): Promise<{ id: string; accessToken?: string; rootId?: string; remainingUses: number; requiresSubscription: boolean }> {
  const response = await fetch(apiUrl(`/image-generation/retry/${id}`), {
    method: 'POST',
    headers: apiHeaders('application/json'),
    body: JSON.stringify({ accessToken, clientId, trademark, newsletterTest })
  });

  if (!response.ok) {
    throw await apiError(response);
  }

  return response.json();
}

export async function subscribeToNewsletter(email: string, clientId: string): Promise<{ subscribed: true }> {
  const response = await fetch(apiUrl('/shopify/newsletter/subscribe'), {
    method: 'POST',
    headers: apiHeaders('application/json'),
    body: JSON.stringify({ email, clientId })
  });

  if (!response.ok) {
    throw await apiError(response);
  }

  return response.json();
}

export async function createPaymentLink(id: string, payload: PaymentLinkPayload): Promise<CheckoutPayload> {
  const response = await fetch(apiUrl(`/image-generation/payment-link/${id}`), {
    method: 'POST',
    headers: apiHeaders('application/json'),
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    throw await apiError(response);
  }

  return response.json();
}
